import React from "react";
import { useQuery } from "@tanstack/react-query";
import { http, fmtGBP } from "@/lib/api";
import { useAuth, ROLE_LABEL } from "@/lib/auth";
import { Link } from "react-router-dom";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { ArrowRight, Warning, ClipboardText, Clock, CurrencyGbp, CheckCircle } from "@phosphor-icons/react";

function Kpi({ label, value, sub, tone = "default", icon: Icon, testid }) {
  const tones = {
    default: "text-slate-400",
    warn: "text-amber-500",
    crit: "text-rose-500",
    ok: "text-emerald-500",
  };
  return (
    <div data-testid={testid} className="rmms-card rmms-card-hover p-5 h-full flex flex-col">
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        {Icon && <Icon size={18} weight="fill" className={tones[tone]} />}
      </div>
      <div className="mt-3 font-mono-data text-3xl font-semibold tracking-tight tabular-nums text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data: s } = useQuery({ queryKey: ["summary"], queryFn: () => http.get("/reports/summary").then(r => r.data) });
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs", { limit: 5 }], queryFn: () => http.get("/jobs").then(r => r.data) });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });

  const storeMap = Object.fromEntries(stores.map(x => [x.id, x.name]));

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="overline">Operations</div>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight mt-1">Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1">Welcome back, <span className="font-medium text-slate-900">{user?.name}</span> · <span className="text-slate-500">{ROLE_LABEL[user?.role]}</span></p>
        </div>
        <Link to="/jobs/new" data-testid="dashboard-create-job-cta"
          className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium transition-colors"
          style={{ background: "#002FA7" }}>
          Create job <ArrowRight size={14} weight="bold" />
        </Link>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi testid="kpi-open" label="Open Jobs" value={s?.open_count ?? "—"} sub={<><ClipboardText size={11} className="inline -mt-0.5" /> in flight</>} icon={ClipboardText} />
        <Kpi testid="kpi-pending" label="Pending Approval" value={s?.pending_approval ?? "—"} sub="awaiting decision" tone="warn" icon={Clock} />
        <Kpi testid="kpi-overdue" label="Overdue" value={s?.overdue ?? "—"} sub={<><Warning size={11} className="inline -mt-0.5" /> past due date</>} tone={s?.overdue > 0 ? "crit" : "default"} icon={Warning} />
        <Kpi testid="kpi-spend-mtd" label="Spend MTD" value={fmtGBP(s?.spend_mtd || 0)} sub={<><CurrencyGbp size={11} className="inline -mt-0.5" /> actual costs</>} tone="ok" icon={CurrencyGbp} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rmms-card">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="font-heading font-semibold text-sm">Recent jobs</div>
            <Link to="/jobs" data-testid="view-all-jobs-link" className="text-xs text-[#002FA7] hover:underline">View all →</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {(jobs || []).slice(0, 7).map((j) => (
              <Link to={`/jobs/${j.id}`} key={j.id} data-testid={`dashboard-job-row-${j.job_number}`}
                className="grid grid-cols-[1fr_auto] items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-mono-data text-slate-500">{j.job_number}</span>
                    <PriorityBadge priority={j.priority} />
                  </div>
                  <div className="text-sm font-medium truncate">{j.title}</div>
                  <div className="text-xs text-slate-500 truncate">{storeMap[j.store_id] || "—"}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono-data text-sm tabular-nums">{fmtGBP(j.estimated_cost)}</span>
                  <StatusBadge status={j.status} />
                </div>
              </Link>
            ))}
            {jobs?.length === 0 && <div className="px-5 py-10 text-center text-sm text-slate-500">No jobs yet.</div>}
          </div>
        </div>

        <div className="rmms-card">
          <div className="px-5 py-3 border-b border-slate-200 font-heading font-semibold text-sm">Status mix</div>
          <div className="p-5 space-y-3">
            {s?.by_status && Object.entries(s.by_status).map(([k, v]) => {
              const total = Object.values(s.by_status).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((v / total) * 100);
              return (
                <div key={k} data-testid={`status-mix-${k}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <StatusBadge status={k} />
                    <span className="font-mono-data text-sm tabular-nums">{v} <span className="text-slate-400">({pct}%)</span></span>
                  </div>
                  <div className="h-1 bg-slate-100 overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, background: "#002FA7" }} />
                  </div>
                </div>
              );
            })}
            {!s && <div className="text-sm text-slate-400">Loading…</div>}
          </div>
        </div>
      </section>

      {s?.spend_by_store?.length > 0 && (
        <section className="rmms-card mt-4">
          <div className="px-5 py-3 border-b border-slate-200 font-heading font-semibold text-sm flex items-center gap-2">
            <CurrencyGbp size={14} /> Spend by store
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-2">
            {s.spend_by_store.map((s2) => {
              const max = Math.max(...s.spend_by_store.map(x => x.spend));
              const pct = Math.round((s2.spend / max) * 100);
              return (
                <div key={s2.store_id} data-testid={`spend-store-${s2.store_id}`} className="grid grid-cols-[1fr_auto] gap-2 items-center">
                  <div>
                    <div className="text-sm">{s2.store_name}</div>
                    <div className="h-1.5 mt-1 bg-slate-100"><div className="h-full" style={{ width: `${pct}%`, background: "#002FA7" }} /></div>
                  </div>
                  <div className="font-mono-data text-sm tabular-nums">{fmtGBP(s2.spend)}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
